<template>
  <div class="w-full flex items-center border-1 border-solid border-primary5 pa-4 b-rounded-2 gap-4">
    <div
      class="flex items-center bg-primary6 color-primary4 w-[100px] justify-center flex-self-stretch b-rounded-1 shrink-0"
    >
      <CIcon :fa-type="icon.type" :icon="icon.name" size="24px" />
    </div>

    <div class="flex flex-col justify-center gap-2 truncate w-full">
      <div class="font-size-[18px] font-700 tracking-[0.4px] truncate line-height-[1.2]">{{ content.name }}</div>

      <div class="flex gap-1 items-center line-height-[1.2] color-text2">
        <CIcon :fa-type="icon.type" fixed-width :icon="icon.name" size="12px" />{{ typeName }}
      </div>

      <!--      todo desc for blocks and apps-->
      <!--      <div class="tracking-[0.4px] font-size-[14px] color-text2"></div>-->
    </div>

    <div class="flex items-center gap-2">
      <CButton v-tooltip="t('canvasUI.agendaSelector.editContent')" circle @click="emit('edit')">
        <CIcon icon="pen" />
      </CButton>

      <CButton
        v-tooltip="t('canvasUI.agendaSelector.deleteContent')"
        circle
        secondary
        type="error"
        @click="emit('delete')"
      >
        <CIcon icon="trash" />
      </CButton>

      <CDropdown
        :options="getActionsMenu()"
        :themeOverrides="dropdownThemeOverrides"
        trigger="click"
        @select="handleActions"
      >
        <CButton :circle="true">
          <CIcon icon="ellipsis" size="16px" />
        </CButton>
      </CDropdown>
    </div>
  </div>
</template>

<script lang="ts" setup>
import { computed, h } from 'vue'
import { useI18n } from 'vue-i18n'
import type { DropdownMixedOption } from 'naive-ui/es/dropdown/src/interface'
import CButton from '@lib/components/CButton/CButton.vue'
import CDropdown from '@lib/components/CDropdown/CDropdown.vue'
import CIcon from '@lib/components/CIcon/CIcon.vue'
import type { FontAwesomeType } from '@canvas-builder/types/font-awesome.types'
import { ComponentIcon, componentIconType } from '@canvas-builder/types/canvas'
import type { ValuesOf } from '@lib/types/common'
import type { CollectionPlayer } from '@collection-player/types/collection-player.type'
import { dropdownThemeOverrides } from '@agenda-selector/utils/theme-overrides.util'
import type { ContentType } from '@agenda-selector/constants'

enum ReorderMenu {
  MoveUp = 'moveUp',
  MoveDown = 'moveDown',
  MoveToGroup = 'moveToGroup',
}

const props = defineProps<{
  content: CollectionPlayer
  type: ValuesOf<ContentType>
  isFirst?: boolean
  isLast?: boolean
  disabledMoveToGroup?: boolean
}>()

const emit = defineEmits<{
  edit: []
  delete: []
  moveUp: []
  moveDown: []
  moveToGroup: []
}>()

const { t } = useI18n()

const icon = computed<{ name: string; type?: FontAwesomeType }>(() => {
  switch (props.type) {
    case 'block':
      return { name: 'regular-objects-column-circle-plus', type: 'fa-kit' }
    case 'application':
      return { name: 'regular-rocket-circle-plus', type: 'fa-kit' }
    case 'collection':
      return { name: ComponentIcon.CollectionPlayer, type: componentIconType.CollectionPlayer }
    default:
      return { name: 'question' }
  }
})

const typeName = computed(() => {
  switch (props.type) {
    case 'block':
      return t('canvasUI.agendaSelector.contentType.block')
    case 'application':
      return t('canvasUI.agendaSelector.contentType.application')
    case 'collection':
      return t('canvasUI.agendaSelector.contentType.collection')
    default:
      return ''
  }
})

function handleActions(key: ValuesOf<ReorderMenu>) {
  switch (key) {
    case ReorderMenu.MoveUp:
      emit(ReorderMenu.MoveUp)
      break
    case ReorderMenu.MoveDown:
      emit(ReorderMenu.MoveDown)
      break
    case ReorderMenu.MoveToGroup:
      emit(ReorderMenu.MoveToGroup)
      break
    default:
      break
  }
}

function getActionsMenu() {
  const actionsMenu: DropdownMixedOption[] = [
    {
      disabled: props.isFirst,
      label: () => h('div', { class: 'font-size-4' }, t('canvasUI.agendaSelector.moveItem.moveUp')),
      key: ReorderMenu.MoveUp,
      icon: () => h(CIcon, { icon: 'arrow-up' }),
    },
    {
      disabled: props.isLast,
      label: () => h('div', { class: 'font-size-4' }, t('canvasUI.agendaSelector.moveItem.moveDown')),
      key: ReorderMenu.MoveDown,
      icon: () => h(CIcon, { icon: 'arrow-down' }),
    },
    {
      disabled: props.disabledMoveToGroup,
      label: () => h('div', { class: 'font-size-4' }, t('canvasUI.agendaSelector.moveItem.moveToGroup')),
      key: ReorderMenu.MoveToGroup,
      icon: () => h(CIcon, { icon: 'arrows-from-dotted-line' }),
    },
  ]

  return actionsMenu
}
</script>
